import { BackgroundCheckAnalytics, BackgroundCheckScreeningStatus } from '@/backend/features/background-check';
import { BackgroundCheckQuota } from '@/backend/features/background-check/defaults';
import { screeningsRoutes } from '@/backend/routes';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { StatisticsCardProps } from '@juvare/common-web-ui/StatisticsCard';
import SafetyCheck from '@juvare/common-web-ui/icons/SafetyCheck';
import { SettingColumns, SettingData } from '@juvare/platform-sdk-react/backend/features/editable-settings/shared';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import AddModerator from '@mui/icons-material/AddModerator';
import RemoveModerator from '@mui/icons-material/RemoveModerator';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import { Box, Stack, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ScreeningsSection } from '../../../screenings';
import { BackgroundCheckSummaryHeading } from './components/BackgroundCheckSummaryHeading';
import { BackgroundCheckToolbar } from './components/BackgroundCheckToolbar';

export interface BackgroundCheckSummaryProps {
    analyticsData: BackgroundCheckAnalytics[];
}

export type StatusCardConfig = Omit<StatisticsCardProps, 'content'>;
export type BackgroundCheckStatusCardsConfig = Record<BackgroundCheckScreeningStatus, StatusCardConfig>;

const getCardsConfig = (t: TFunction<'rms'>): BackgroundCheckStatusCardsConfig => ({
    ready: {
        icon: <AddModerator htmlColor={themeColors.accent.grey.base} />,
        headingText: t('backgroundCheck.labels.ready'),
        headingColor: 'main',
        'data-test': 'ready-statistics-card',
    },
    processing: {
        icon: <SafetyCheck htmlColor={themeColors.accent.blue.base} />,
        headingText: t('backgroundCheck.labels.processing'),
        headingColor: 'main',
        'data-test': 'processing-statistics-card',
    },
    clear: {
        icon: <VerifiedUser htmlColor={themeColors.accent.green.base} />,
        headingText: t('backgroundCheck.labels.cleared'),
        headingColor: 'main',
        'data-test': 'cleared-statistics-card',
    },
    failed: {
        icon: <RemoveModerator htmlColor={themeColors.accent.red.base} />,
        headingText: t('backgroundCheck.labels.failed'),
        headingColor: 'main',
        'data-test': 'failed-statistics-card',
    },
});

export const BackgroundCheckSummary: React.FC<BackgroundCheckSummaryProps> = ({ analyticsData }) => {
    const { t } = useTranslation();
    const { backgroundCheckQuota } = useSelector(getSettings);
    const [quota, setQuota] = useState<BackgroundCheckQuota | undefined>();
    const settingStorage = useStorageClient<SettingColumns, SettingData>('setting');
    const isBackgroundCheckQuotaEnabled = Object.values(backgroundCheckQuota).some((value) => value.enabled);
    const statusCards = getCardsConfig(t);

    const fetchQuota = useCallback(() => {
        settingStorage
            .list({
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: 'backgroundCheckQuota',
                    },
                ],
            })
            .then((data) => {
                const quotaValue = JSON.parse(data.rows[0].columns.value);
                setQuota(quotaValue);
            });
    }, [settingStorage]);

    useEffect(() => {
        if (isBackgroundCheckQuotaEnabled) {
            fetchQuota();
        }
    }, [isBackgroundCheckQuotaEnabled, fetchQuota]);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: {
                        xs: 'flex-start',
                        md: 'center',
                    },
                    flexDirection: {
                        xs: 'column',
                        md: 'row',
                    },
                    justifyContent: { md: 'space-between' },
                    gap: 3,
                    mb: 3,
                }}
            >
                <Typography variant='subtitle1' color={themeColors.text.main}>
                    {t('pages.screenings.backgroundChecks.title')}
                </Typography>
                <BackgroundCheckToolbar updateQuota={fetchQuota} />
            </Box>
            <Stack sx={{ width: '100%' }} gap={2}>
                {analyticsData.map((analytics) => (
                    <ScreeningsSection
                        key={analytics.label}
                        statusCardsConfig={statusCards}
                        analytics={analytics}
                        detailsLink={screeningsRoutes.backgroundChecks}
                        sectionHeading={<BackgroundCheckSummaryHeading analytics={analytics} quota={quota} />}
                    />
                ))}
            </Stack>
        </Box>
    );
};
